/**
 * Schedule toolbar filters: empty-project visibility, weekends, project, work
 * scope, and worker. The multi-select filters combine (AND) and cross-curate
 * each other's options. Thin wrapper mapping Schedule state onto the shared
 * {@link FiltersPanel}.
 */
import { useCallback } from "react";
import { PLANNING_TOOLTIP_MESSAGES } from "../../../config";
import { useScheduleContext } from "../../../context/domain/planning/ScheduleContext";
import { FiltersPanel } from "../../../components/ui";

/**
 * Filters button and popover; reads filter state from {@link useScheduleContext}.
 *
 * @returns {JSX.Element}
 */
export default function ScheduleFiltersPanel() {
  const {
    showWeekends,
    setShowWeekends,
    hideEmptyProjects,
    setHideEmptyProjects,
    filterProjectId,
    setFilterProjectId,
    filterWorkScopeId,
    setFilterWorkScopeId,
    filterWorkerId,
    setFilterWorkerId,
    scheduleFilterProjectOptions,
    scheduleFilterScopeOptions,
    scheduleFilterWorkerOptions,
  } = useScheduleContext();

  const toolbarFilterActive =
    showWeekends ||
    !hideEmptyProjects ||
    filterProjectId.length > 0 ||
    filterWorkScopeId.length > 0 ||
    filterWorkerId.length > 0;

  const clearAllFilters = useCallback(() => {
    setShowWeekends(false);
    setHideEmptyProjects(true);
    setFilterProjectId([]);
    setFilterWorkScopeId([]);
    setFilterWorkerId([]);
  }, [
    setShowWeekends,
    setHideEmptyProjects,
    setFilterProjectId,
    setFilterWorkScopeId,
    setFilterWorkerId,
  ]);

  return (
    <FiltersPanel
      ariaLabel="Schedule filters"
      isActive={toolbarFilterActive}
      onClearAll={clearAllFilters}
      iconOnly
      checkboxes={[
        {
          id: "show-empty-projects",
          label: "Show Empty Projects",
          title: PLANNING_TOOLTIP_MESSAGES.showEmptyProjects,
          checked: !hideEmptyProjects,
          onChange: (checked) => setHideEmptyProjects(!checked),
        },
        {
          id: "show-weekends",
          label: "Show Weekends",
          checked: showWeekends,
          onChange: setShowWeekends,
        },
      ]}
      selects={[
        {
          id: "project",
          label: "Project",
          value: filterProjectId,
          options: scheduleFilterProjectOptions,
          onChange: setFilterProjectId,
          placeholder: "Search projects...",
          emptyPlaceholder: "Search projects...",
        },
        {
          id: "work-scope",
          label: "Work scope",
          value: filterWorkScopeId,
          options: scheduleFilterScopeOptions,
          onChange: setFilterWorkScopeId,
          placeholder: "Search scopes...",
          emptyPlaceholder: "Search scopes...",
        },
        {
          id: "worker",
          label: "Worker",
          value: filterWorkerId,
          options: scheduleFilterWorkerOptions,
          onChange: setFilterWorkerId,
          placeholder: "Search workers...",
          emptyPlaceholder: "Search workers...",
        },
      ]}
    />
  );
}
